import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, notification, Form, Space, DatePicker,Tooltip ,Input,Select} from "antd";
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal";
import GenericViewModal from "../components/GenericViewModal";
import {
  fetchPatientsRequest,
  createPatientRequest,
  updatePatientRequest,
  deletePatientRequest,
} from "../redux-saga/actions/patientActions";
import { useParams } from "react-router-dom";
import ProjectNavbar from "../components/ProjectNavbar";
import moment from "moment";
import { updateCurrentProjectId } from "../redux-saga/actions/customerActions";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {IconWheelchair} from '@tabler/icons-react';


const { Option } = Select;

const sexOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Others", value: "others" },
];

const patientModalInputs = [
  { label: "Patient Name", name: "patientName", type: "text" },
  { label: "Date of Birth", name: "dob", type: "date", component: DatePicker },
  {
    label: "Sex",
    name: "sex",
    type: "select",
    component: Select,
    options: sexOptions,
  },
  { label: "Status", name: "patientStatus", type: "text" },
  { label: "Address", name: "address", type: "text" },
  { label: "City", name: "city", type: "text" },
  { label: "State", name: "state", type: "text" },
  { label: "Telephone No", name: "telephoneNo", type: "text" },
];

const PatientPage = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const patients = useSelector((state) => state.patient.patients?.data);
  const loading = useSelector((state) => state.patient.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [form] = Form.useForm();
  const [viewingPatient, setViewingPatient] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  const metaData = useSelector((state) => state.patient.patients?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchPatientsRequest(id,{ page: currentPage, limit: 2, search: searchTerm }));
    dispatch(updateCurrentProjectId(id));
  }, [dispatch, id,currentPage, searchTerm]);

  const handleCreateOrEdit = (values) => {
    // Ensure dob is a properly formatted string before sending to backend
    const formattedValues = {
      ...values,
      dob: values.dob ? values.dob.format("YYYY-MM-DD") : null, // Convert dob to string
      sex: String(values.sex),
      state: String(values.state),
    };

    // Dispatch the appropriate action
    if (editingPatient) {
      dispatch(updatePatientRequest(editingPatient.id, formattedValues, id));
    } else {
      dispatch(createPatientRequest(formattedValues, id));
    }

    notification.success({ message: "Success", description: "Patient saved successfully." });
    setIsModalVisible(false);
    setEditingPatient(null);
    form.resetFields();
  };

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      dob: record.dob ? moment(record.dob).format("YYYY-MM-DD HH:mm:ss") : null,
      createdAt: record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt: record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
    };
    setViewingPatient(formattedRecord);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    const recordWithFormattedDate = {
      ...record,
      dob: record.dob ? moment(record.dob, "YYYY-MM-DD") : null, // Convert if dob exists
    };
    setEditingPatient(recordWithFormattedDate);
    setIsModalVisible(true);
    form.setFieldsValue(recordWithFormattedDate);
  };

  const handleDelete = (record) => {
    dispatch(deletePatientRequest(id,record.id));
    notification.success({ message: "Deleted", description: "Patient deleted successfully." });
  };

  const columnsConfig = [
    { title: "Patient Name", dataIndex: "patientName" },
    { title: "DOB", dataIndex: "dob", render: (text) => (text ? moment(text).format("YYYY-MM-DD") : "-") },
    { title: "Sex", dataIndex: "sex" },
    { title: "Status", dataIndex: "patientStatus" },
    { title: "Address", dataIndex: "address" },
    { title: "City", dataIndex: "city" },
    { title: "State", dataIndex: "state" },
    { title: "Telephone No", dataIndex: "telephoneNo" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];
 

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <ProjectNavbar />
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <IconWheelchair style={{ fontSize: "24px", color: "black" }} />
                <h2 style={{ margin: 0 }}>Patient</h2>
            </div>

            <div style={{ display: "flex", justifyContent: "end", marginBottom: 11 }}>
              <Button type="primary" onClick={() => setIsModalVisible(true)}>
                Create Patient
              </Button>
            </div>
        </div>
        <Input
          placeholder="Search Patient"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <GenericTable data={patients} columnsConfig={columnsConfig} loading={loading} pagination={{
            current: metaData.page || 1,
            pageSize: metaData.limit || 2,
            total: metaData.total || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              dispatch(fetchPatientsRequest({ page, limit: pageSize, search: searchTerm }));
            },
          }} />

        {/* View Modal for Viewing Patient Details */}
        {isViewModalVisible && (
          <GenericViewModal
            isVisible={isViewModalVisible}
            handleCancel={() => setIsViewModalVisible(false)}
            title="View Patient"
            data={viewingPatient}
            footerButtons={["Close"]}
            onButtonClick={() => setIsViewModalVisible(false)}
          />
        )}

        {/* Modal for Create and Edit */}
        {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingPatient(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconWheelchair style={{ color: "white", marginRight: "8px" }} />
                {editingPatient ? "Edit Patient" : "Create Patient"}
              </span>
            }
            inputs={patientModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingPatient || {}}
          />
        )}
      </div>
    </div>
  );
};

export default PatientPage;
